/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import {
    type BaseHttpResponse, type CompositeRestParam, type HardwareFeatureList, type HttpResponse, type License,
    type Polling, type Rest,
type TimeZoneConfig, CORE_CONFIG, ERROR_OK, EventService, HttpService, I18nService,
    LocalStorageService, PermissionService, PollingService, PROMPT_STORAGE_KEY, TimeService, ToastService,
} from '@dm/core';
import { FeatureConfig } from 'app/config/feature';
import { ShardSwitch, SWITCH } from 'app/config/switch';
import { type Subscriber, empty, Observable, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';

import { hardwareEqualToLicenseMap, hardwareFeatureAndInfoMap } from '../constants/hardware-feature.const';
import { LicenseKey } from '../constants/license-key';
import { type ActiveHardwareFeature, type AlarmCountAndRecent, type DisablePromptSwitch, type System, type SystemDstConfig, type SystemTimeZone, type SystemUtcTime, type UserSecurityPolicy } from '../types/mo';

export interface TimeData {
    time: SystemUtcTime;
    timeZone: SystemTimeZone;
    dstConfig?: SystemDstConfig;
}

export enum ScaleOutType {
    directConnection = '0', // 直连组网
    switchNetworking = '1', // 交换机组网
    singleEngine = '2' // 单引擎，无组网类型
}

@Injectable({ providedIn: 'root' })
export class GlobalDataService {
    /**
     * 告警数据
     */
    private alarmData: AlarmCountAndRecent = {
        critical: 0,
        major: 0,
        warning: 0,
        recent: [],
        isFetched: false,
    };
    // 波动检测总数
    anomalyCount = 0;
    // 首页是否展示波动检测
    showAnomalyInHome = false;
    /**
     * 设备信息
     */
    private systemInfo: System;
    /**
     * 用户安全策略
     */
    private userSecurityPolicy: UserSecurityPolicy;
    /**
     * 时间轮询
     */
    private timePolling: Polling;

    oldSupportNas = SWITCH.SUPPORT_NAS;

    constructor(
        private eventService: EventService,
        private httpService: HttpService,
        private pollingService: PollingService,
        private permissionService: PermissionService,
        private timeService: TimeService,
        private i18nService: I18nService,
        private localStorageService: LocalStorageService,
        private toastService: ToastService,

    ) { }

    /**
     * 初始化全局数据，需要等鉴权过后再启动
     */
    init(): void {
        const compositeRests: CompositeRestParam = {
            critical: {
                rest: this.getAlarmCountRest({
                    displayStart: 0,
                    iDisplayLength: 1,
                    filter: 'level::Critical',
                }),
                method: 'GET',
            },
            major: {
                rest: this.getAlarmCountRest({
                    displayStart: 0,
                    iDisplayLength: 1,
                    filter: 'level::Major',
                }),
                method: 'GET',
            },
            warning: {
                rest: this.getAlarmCountRest({
                    displayStart: 0,
                    iDisplayLength: 1,
                    filter: 'level::Warning',
                }),
                method: 'GET',
            },
            total: {
                rest: this.getAlarmCountRest({
                    displayStart: 0,
                    iDisplayLength: 1,
                }),
                method: 'GET',
            },
            systemInfo: {
                rest: this.getSystemInfoRest(),
                method: 'GET',
            },
        };

        // 有alarm_R权限再轮询，否则底层会因为报错冲日志
        if (this.permissionService.hasPermission('alarm_R')) {
            compositeRests.recent = {
                rest: this.getCurrentAlarmRest({
                    range: '[0-10]',
                    sortby: ['startTime', 'd'],
                    timeConversion: 0,
                }),
                method: 'GET',
            };
        }

        this.pollingService.addCombo(compositeRests, 15, true)
            .immediate()
            .subscribe(({ critical, major, warning, total, recent, systemInfo }) => {
                this.alarmData.critical = Number(critical.data.COUNT) || 0;
                this.alarmData.major = Number(major.data.COUNT) || 0;
                this.alarmData.warning = Number(warning.data.COUNT) || 0;
                this.alarmData.total = Number(total.data.COUNT) || 0;
                this.alarmData.recent = recent && recent.result.code === ERROR_OK ? recent.data || [] : [];
                this.alarmData.isFetched = true;

                this.eventService.emit('alarmUpdate', {
                    ...this.alarmData,
                    recent: [...this.alarmData.recent],
                });

                this.setSystemInfo(systemInfo.data);
            });

        // 轮询安全策略
        this.pollingService.add(this.httpService.makeUrl('securityPolicy.get'), 15)
            .subscribe(({ data }) => {
                this.userSecurityPolicy = data;
                this.eventService.emit('userSecurityPolicyUpdate', {
                    ...this.userSecurityPolicy,
                });
            });

        if (FeatureConfig.IsHideSmartGui) {
            SWITCH.SUPPORT_SMART_GUI = false;
        }

        // V6-dashboard监控NAS性能指标增强【Dorado 新融合支持】
        if (!FeatureConfig.IsSupportDashboardIndicatorEnhance) {
            ShardSwitch.SHOW_DASHBOARD_INDICATOR_ENHANCE = false;
        }

        //微存储不支持支持系统级smartQos特性
        if (!FeatureConfig.IsSupportSystemSmartQos) {
            ShardSwitch.SHOW_SYSTEM_SMARTQOS = false;
        }

        // 轮询异常检测数据
        if (!SWITCH.IS_VSTORE_SYSTEM && SWITCH.SUPPORT_ABNORMAL_DETECTION && this.permissionService.checkShow(['performance_R'])) {
            this.pollingService.add(this.httpService.makeUrl('performanceData.getAnomalyCount', 'V2'), 15)
                .immediate()
                .subscribe((res: BaseHttpResponse) => {
                    // 未配置PMS LUN时,不展示异常检测数量图标
                    if ((res.result && res.result.code === 503) || !res.data) {
                        this.eventService.emit('modifyPMSSwitch', false);
                    } else {
                        const { data: { abnormalTotal } } = res;
                        this.anomalyCount = abnormalTotal;
                        this.showAnomalyInHome = SWITCH.SUPPORT_ABNORMAL_DETECTION;
                        this.eventService.emit('getAnomalyCount', abnormalTotal);
                        this.eventService.emit('modifyPMSSwitch', true);
                    }
                }, () => {
                    this.eventService.emit('modifyPMSSwitch', false);
                });
        }

        // 设置非危险类高危提示屏蔽功能开关标识
        if (ShardSwitch.DISABLE_PROMPT) {
            this.setdisablePromptTag();
        }


    }

    /**
     * 获取当前缓存的告警数据
     */
    getAlarmData(): AlarmCountAndRecent {
        return {
            ...this.alarmData,
            recent: [...this.alarmData.recent],
        };
    }

    /**
     * 设置系统信息
     */
    setSystemInfo(systemInfo: System): void {
        this.systemInfo = systemInfo;
        this.eventService.emit('systemInfoUpdate', {
            ...this.systemInfo,
        });
    }

    /**
     * 获取当前告警rest
     * @param params
     */
    private getCurrentAlarmRest(params): Rest {
        return this.httpService.makeUrl('alarm.getCurrentAlarm').setParams(params);
    }

    /**
     * 获取告警总数rest
     * @param params
     */
    private getAlarmCountRest(params): Rest {
        return this.httpService.makeUrl('alarm.getAlarmCount').setParams(params);
    }

    /**
     * 查询设备信息rest
     */
    private getSystemInfoRest(): Rest {
        return this.httpService.makeUrl('system.get');
    }

    /**
     * 获取设备信息
     */
    getSystemInfo(): System {
        return this.systemInfo ? { ...this.systemInfo } : this.systemInfo;
    }

    /**
     * 异步获取设备信息
     */
    getSystemInfoSync(): Observable<System> {
        if (this.systemInfo) {
            return of(this.getSystemInfo());
        }
        return this.httpService.get(this.getSystemInfoRest()).pipe(
            map(({ data }) => {
                this.setSystemInfo(data);
                return this.getSystemInfo();
            }),
        );
    }

    /**
     * 获取用户安全策略
     */
    getUserSecurityPolicy(): UserSecurityPolicy {
        return this.userSecurityPolicy ? { ...this.userSecurityPolicy } : this.userSecurityPolicy;
    }

    /**
     * 初始化License权限信息
     */
    initLicenseFeature(): Observable<any> {
        return this.httpService.get(this.httpService.makeUrl('license.getStatus'))
            .pipe(
                catchError((err) => of(err)),
                tap(({ data }: { data: License[] }) => {
                    const license: { [key: string]: License } = {};
                    if (data?.length) {
                        data.forEach((o) => {
                            const key = Object.keys(o).find((licenseKey) => licenseKey !== 'id');
                            license[key] = {
                                id: o.id,
                                name: key,
                                displayName: this.i18nService.get('common.EnumDefine_LicenseFuncFeatureType_' + o.id),
                                state: o[key],
                            };
                        });
                    }

                    // 当前无承诺容量License当成special license处理，为了不去改动现有业务代码，所以这里设置一个默认值
                    // 等这个逻辑稳定之后，把这个默认值去掉，然后再去改业务代码
                    // 微存储不需要添加默认的可得容量license
                    if (!FeatureConfig.IsHideGuaranteedCapacity &&
                        !license[LicenseKey.GuaranteedCapacity] &&
                        !license[LicenseKey.SpecialLicense]) {
                        license[LicenseKey.SpecialLicense] = {
                            id: 105,
                            name: LicenseKey.SpecialLicense,
                            displayName: this.i18nService.get('common.EnumDefine_LicenseFuncFeatureType_105'),
                            state: 1,
                        };
                    }

                    // 缓存当前产品是否支持nas, 3000是否支持nas由此值判断
                    SWITCH.PRODUCT_SUPPORT_NAS = this.oldSupportNas;

                    // 如果没有NAS License，则屏蔽NAS
                    if (!license[LicenseKey.NAS]) {
                        SWITCH.SUPPORT_NAS = false;
                        SWITCH.LICENSE_SUPPORT_NAS = false;
                    } else {
                        SWITCH.SUPPORT_NAS = this.oldSupportNas;
                        SWITCH.LICENSE_SUPPORT_NAS = true;
                    }

                    this.getInternalDeviceInfo();

                    // 将lun术语替换为namespace场景无SmartThin License，但是默认支持san相关的业务，617需求需要根据SmartThin来控制san特性
                    // 为了不去改动现有业务代码，所以这里为将lun术语替换为namespace场景设置一个默认值
                    if (FeatureConfig.IsReplaceStrLunWithNamespace) {
                        license[LicenseKey.SmartThin] = {
                            id: 25,
                            displayName: 'commonm.common.EnumDefine_LicenseFuncFeatureType_25',
                            name: LicenseKey.SmartThin,
                            state: 1,
                        };
                    }
                    this.permissionService.setLicense(license);
                }),
            );
    }

    /**
     * 初始化硬件管控特性信息
     */
    initHardwareFeature(): Observable<{ data: ActiveHardwareFeature }> {
        return this.httpService.get(this.httpService.makeUrl('hardwareFeature.getStatus'), { doException: false })
            .pipe(
                catchError((err) => of(err)),
                tap(({ data }: { data: ActiveHardwareFeature }) => {
                    if (!data) {
                        return;
                    }

                    const hardwareFeatureList: HardwareFeatureList = {};
                    Object.keys(data).forEach((key: string) => {
                        // 硬件管控特性状态是布尔值，硬件管控特性信息是数组
                        if (typeof data[key] === 'boolean') {
                            const equalLicenseList: License[] = hardwareEqualToLicenseMap[key] || [];
                            // 构造虚拟证书的名称
                            equalLicenseList.forEach((equalLicense: License) => {
                                equalLicense.displayName =
                                    this.i18nService.get(`common.EnumDefine_LicenseFuncFeatureType_${equalLicense.id}`);
                            });
                            // 构造硬件特性证书列表
                            hardwareFeatureList[key] = {
                                name: key,
                                valid: data[key],
                                equalLicenseList,
                                info: data[hardwareFeatureAndInfoMap[key]],
                            };
                        }
                    });

                    this.permissionService.setHardwareFeature(hardwareFeatureList);
                }),
            );
    }

    /**
     * 初始化系统时间。每30秒轮询一次
     */
    initTime(): Observable<void> {
        return Observable.create((observer: Subscriber<void>) => {
            const rests: CompositeRestParam = {
                time: {
                    rest: this.httpService.makeUrl('systemUtcTime.get'),
                    method: 'GET',
                },
                timeZone: {
                    rest: this.httpService.makeUrl('systemTimeZone.get'),
                    method: 'GET',
                },
            };

            /**
             * 第一次查询完成后就next保证能进入系统
             */
            const resolve = (): void => {
                if (!observer.closed) {
                    sub$.unsubscribe();
                    sub$ = null;
                    observer.next();
                    observer.complete();
                }
            };

            this.timePolling = this.pollingService.addCombo(rests, 30, true);
            this.timePolling.pipe(
                flatMap(({ time, timeZone }: { [key: string]: HttpResponse }) => {
                    if (time.result.code !== ERROR_OK || timeZone.result.code !== ERROR_OK) {
                        resolve();
                        return empty();
                    }

                    const timeZoneData: SystemTimeZone = timeZone.data[0];
                    const res: TimeData = {
                        time: time.data,
                        timeZone: timeZoneData,
                    };

                    if (timeZoneData.CMO_SYS_TIME_ZONE_USE_DST === '1') {
                        return this.getDstConfig(timeZoneData.CMO_SYS_TIME_ZONE_NAME)
                            .pipe(
                                map(({ data }) => {
                                    res.dstConfig = data;
                                    return res;
                                }),
                            );
                    } else {
                        return of(res);
                    }
                }),
            ).subscribe(({ time, timeZone, dstConfig }) => {
                const timeZoneConfig: TimeZoneConfig = {
                    timeZone: timeZone.CMO_SYS_TIME_ZONE,
                    timeZoneName: timeZone.CMO_SYS_TIME_ZONE_NAME,
                    useDst: timeZone.CMO_SYS_TIME_ZONE_USE_DST === '1',
                };

                if (timeZoneConfig.useDst) {
                    timeZoneConfig.dstOffset = Number(dstConfig.CMO_SYS_DST_CONF_ADJUST_TIME);
                    timeZoneConfig.dstTimeBegin = dstConfig.CMO_SYS_DST_CONF_DATE_TIME_BEGIN;
                    timeZoneConfig.dstTimeEnd = dstConfig.CMO_SYS_DST_CONF_DATE_TIME_END;
                }

                this.timeService.setTime(Number(time.CMO_SYS_UTC_TIME));
                this.timeService.setTimeZoneConfig(timeZoneConfig);

                resolve();
            }, resolve);
            let sub$ = this.timePolling.err.subscribe(resolve);
        });
    }

    /**
     * 查询夏令时配置
     */
    private getDstConfig(timeZone: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('systemDstConfig.get')
            .setParams({
                CMO_SYS_DST_CONF_TIME_ZONE_NAME: timeZone,
            });
        return this.httpService.get(rest);
    }

    /**
     * 更新时间
     */
    updateTime(): Observable<{
        time: number;
        timeZoneConfig: TimeZoneConfig;
    }> {
        return new Observable((observer) => {
            let observerData = observer;
            this.timePolling.immediate();

            let sub$ = this.timeService.timeZoneChanges.subscribe((timeZoneConfig: TimeZoneConfig) => {
                observerData.next({ time: this.timeService.currTime, timeZoneConfig });
                observerData.complete();

                sub$.unsubscribe();
                errSub$.unsubscribe();
                sub$ = null;
                errSub$ = null;
                observerData = null;
            });

            let errSub$ = this.timePolling.err.subscribe((err) => {
                observerData.error(err);

                sub$.unsubscribe();
                errSub$.unsubscribe();
                sub$ = null;
                errSub$ = null;
                observerData = null;
            });
        });
    }

    /**
     * 查询产品是否为海外型号
     */
    getInternalDeviceInfo(): void {
        this.httpService.get(this.httpService.makeUrl('scaleOut.getInternalDeviceInfo')).subscribe(({ data }) => {
            if (data && data.isOverseas === 'true') {
                SWITCH.IS_DOMESTIC_MODEL = false;
            }
        });
    }

    setdisablePromptTag(): void {
        this.localStorageService.remove(CORE_CONFIG.supportDisablePromptkey);
        this.httpService.get(this.httpService.makeUrl('disablePrompt.get', 'V2'), { doException: false })
            .subscribe(({ data }: { data: DisablePromptSwitch }) => {
                if (data && data.switch === '1') {
                    this.localStorageService.set(CORE_CONFIG.supportDisablePromptkey, true);
                }
            }, () => {
                this.toastService.error(this.i18nService.get('home.NoShowMsgAgain_err_info'));
                this.localStorageService.remove(PROMPT_STORAGE_KEY);
            });
    }
}
